import { Clock, Search, User } from "lucide-react";
import { useState, useEffect } from "react";
import { getSearchesByUser } from "../../../interfaces/signals";
import BONavigation from "../../../components/admin/BONavigation";

interface UserSearches {
    userId: string;
    userEmail: string;
    userName: string;
    totalSearches: number;
    lastSearch: string;
    searches: {
        searchTerm: string;
        context: string;
        createdAt: string;
        resultCount?: number;
    }[];
}

export default function SearchLogs() {
    const [data, setData] = useState<UserSearches[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [days, setDays] = useState(30);
    const [searchFilter, setSearchFilter] = useState("");

    const fetchSearchLogs = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getSearchesByUser(days);
            setData(response.data || []);
        } catch (err: any) {
            console.error("Failed to fetch search logs:", err);
            setError(err?.response?.data?.error || "Failed to load search logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSearchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    const filteredData = data.filter((user) => {
        const searchLower = searchFilter.toLowerCase();
        return (
            user.userEmail.toLowerCase().includes(searchLower) ||
            user.userName.toLowerCase().includes(searchLower) ||
            user.searches.some((s) => s.searchTerm.toLowerCase().includes(searchLower))
        );
    });

    const totalSearches = data.reduce((sum, user) => sum + user.totalSearches, 0);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "À l'instant";
        if (diffMins < 60) return `Il y a ${diffMins}min`;
        if (diffHours < 24) return `Il y a ${diffHours}h`;
        if (diffDays < 7) return `Il y a ${diffDays}j`;
        
        return date.toLocaleDateString('fr-FR', { 
            day: 'numeric', 
            month: 'short', 
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
        });
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-black dark:text-white">
                                Recherches Utilisateurs
                            </h1>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Suivez les recherches effectuées par vos utilisateurs
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <BONavigation />

                {/* Stats Cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="bg-white border border-gray-200 rounded-xs p-4">
                        <p className="text-xs text-gray-600 mb-1">
                            Utilisateurs actifs
                        </p>
                        <p className="text-xl font-semibold text-navy-700 dark:text-white">
                            {data.length}
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xs p-4">
                        <p className="text-xs text-gray-600 mb-1">
                            Total recherches
                        </p>
                        <p className="text-xl font-semibold text-navy-700 dark:text-white">
                            {totalSearches}
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xs p-4">
                        <p className="text-xs text-gray-600 mb-1">
                            Moyenne par utilisateur
                        </p>
                        <p className="text-xl font-semibold text-navy-700 dark:text-white">
                            {data.length > 0 ? Math.round(totalSearches / data.length) : 0}
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                    <div className="flex flex-col gap-4 sm:flex-row">
                        <div className="relative flex-1">
                            <div className="flex items-center gap-2">
                                <Search className="w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Rechercher par email, nom ou terme de recherche..."
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    className="flex-1 border-0 focus:ring-0 focus:outline-none text-xs placeholder:text-gray-400"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Période:
                            </label>
                            <select
                                value={days}
                                onChange={(e) => setDays(Number(e.target.value))}
                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                            >
                                <option value={7}>7 derniers jours</option>
                                <option value={30}>30 derniers jours</option>
                                <option value={90}>90 derniers jours</option>
                                <option value={365}>1 an</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-gray-500 text-xs">Chargement des recherches...</div>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="bg-white border border-red-200 rounded-xs p-4">
                        <p className="text-xs text-red-600">{error}</p>
                    </div>
                )}

                {/* Search Logs List */}
                {!loading && !error && (
                    <div className="space-y-4">
                        {filteredData.length === 0 ? (
                            <div className="text-center py-16">
                                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-sm font-semibold text-navy-700 dark:text-white mb-2">
                                    Aucune recherche trouvée
                                </h3>
                                <p className="text-xs text-gray-500">
                                    Aucun utilisateur n'a effectué de recherche sur cette période.
                                </p>
                            </div>
                        ) : (
                            filteredData.map((user) => (
                                <div
                                    key={user.userId}
                                    className="bg-white border border-gray-200 rounded-xs p-4"
                                >
                                    {/* User Header */}
                                    <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600">
                                                <User className="h-5 w-5 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-semibold text-navy-700 dark:text-white">
                                                    {user.userName}
                                                </h3>
                                                <p className="text-xs text-gray-600">
                                                    {user.userEmail}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <div className="flex items-center gap-1">
                                                <Search className="h-4 w-4" />
                                                <span>{user.totalSearches} recherches</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-4 w-4" />
                                                <span>{formatDate(user.lastSearch)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Searches List */}
                                    <div className="space-y-2">
                                        {user.searches.map((search, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between bg-gray-50 p-3 rounded-xs border border-gray-100"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Search className="h-4 w-4 text-gray-400" />
                                                    <div>
                                                        <p className="text-xs font-medium text-navy-700 dark:text-white">
                                                            {search.searchTerm}
                                                        </p>
                                                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                                                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-sm border border-purple-200 font-medium">
                                                                {search.context}
                                                            </span>
                                                            {search.resultCount !== undefined && (
                                                                <span>
                                                                    {search.resultCount} résultat{search.resultCount > 1 ? 's' : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className="text-[11px] text-gray-500">
                                                    {formatDate(search.createdAt)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
